import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../api";
import { authService } from "../services/auth.service";
import { refreshWithLatestToken, withRefreshLock } from "../utils/refreshLock";
import { accountsService } from "../services/accounts.service";
import { mailsService } from "../services/mails.service";
import {
  prefersDarkQuery,
  resolveThemePreference,
} from "../utils/systemPreferences";
import { getTokenExpirationMs } from "../utils/jwt";

const ACCESS_TOKEN_RENEWAL_MARGIN_MS = 60 * 1000;
const ACCESS_TOKEN_RENEWAL_MIN_DELAY_MS = 10 * 1000;
const ACCESS_TOKEN_RENEWAL_RETRY_DELAY_MS = 30 * 1000;

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Devuelve la ruta del paso de onboarding pendiente para un usuario autenticado,
// o null si no hay nada pendiente (entrar normal a la app). Se deriva de /me.
export function resolveOnboardingPath(authValue) {
  const onboarding = authValue?.onboarding;
  if (!onboarding) return null;

  if (onboarding.next_step === "verify_email") return "/signup/verify-email";
  if (onboarding.next_step === "create_account") return "/signup/create-account";

  const ownedAccountNeedingPlan = (authValue?.billingAccounts || []).find(
    (account) =>
      account?.can_manage_billing && account?.action_required === "select_plan",
  );
  if (ownedAccountNeedingPlan) return "/signup/plan";

  return null;
}

function buildAuthFromMe({ me, access, refresh, previous = null }) {
  const {
    id,
    first_name,
    last_name,
    avatar_url,
    username,
    has_google_oauth,
    has_microsoft_oauth,
    theme_preference,
    language_preference,
    timezone,
    email,
    notifications_enabled,
    follow_up_reminder_days,
    unanswered_mail_reminder_days,
    is_account_admin,
    whatsapp_enabled,
    whatsapp_enabled_account_ids,
    onboarding,
    accounts,
  } = me || {};

  return {
    access,
    refresh,
    onboarding: onboarding || previous?.onboarding || null,
    billingAccounts: Array.isArray(accounts)
      ? accounts
      : previous?.billingAccounts || [],
    user: {
      id: id ?? null,
      name: `${first_name || ""} ${last_name || ""}`.trim(),
      username: username || "",
      avatar: avatar_url || null,
      has_google_oauth: !!has_google_oauth,
      has_microsoft_oauth: !!has_microsoft_oauth,
      theme_preference: theme_preference || "system",
      language_preference: language_preference || "system",
      timezone: timezone || "system",
      email: email || "",
      firstName: first_name || "",
      lastName: last_name || "",
      notifications_enabled: notifications_enabled || false,
      follow_up_reminder_days: follow_up_reminder_days ?? null,
      unanswered_mail_reminder_days: unanswered_mail_reminder_days ?? null,
      is_account_admin: !!is_account_admin,
      whatsapp_enabled: !!whatsapp_enabled,
      whatsapp_enabled_account_ids: Array.isArray(whatsapp_enabled_account_ids)
        ? whatsapp_enabled_account_ids.map(Number)
        : [],
    },
  };
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [supervisedAccounts, setSupervisedAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState(() => {
    const raw = localStorage.getItem("active_account_id");
    return raw ? Number(raw) : null;
  });

  const [loading, setLoading] = useState(true);

  const setAxiosAuthHeader = useCallback((access) => {
    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${access}`;
    } catch {}
  }, []);

  const clearAxiosAuthHeader = useCallback(() => {
    try {
      delete api.defaults.headers.common["Authorization"];
    } catch {}
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {}

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("active_account_id");

    clearAxiosAuthHeader();
    setAuth(null);
    setAccounts([]);
    setSupervisedAccounts([]);
    setActiveAccountId(null);
  }, [clearAxiosAuthHeader]);

  const refreshMe = useCallback(async () => {
    const access = localStorage.getItem("access_token");
    const refresh = localStorage.getItem("refresh_token");
    if (!access) return null;

    setAxiosAuthHeader(access);

    const me = await authService.me({ access });
    const nextAuth = buildAuthFromMe({ me, access, refresh });

    setAuth(nextAuth);
    return nextAuth;
  }, [setAxiosAuthHeader]);

  const themeAppliedRef = useRef(false);
  useEffect(() => {
    const preference = auth?.user?.theme_preference || "system";
    const root = document.documentElement;

    const applyTheme = () => {
      const theme = resolveThemePreference(preference);

      if (themeAppliedRef.current) {
        root.classList.add("theme-transition");
        window.setTimeout(() => root.classList.remove("theme-transition"), 350);
      }
      themeAppliedRef.current = true;

      root.setAttribute("data-theme", theme);
    };

    applyTheme();

    // Solo seguimos el modo del sistema en vivo cuando la preferencia es "system".
    if (preference !== "system") return;
    if (typeof window.matchMedia !== "function") return;

    const media = window.matchMedia(prefersDarkQuery);
    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [auth?.user?.theme_preference]);

  const hasSyncedOnLoginRef = useRef(false);

  useEffect(() => {
    if (!auth?.user?.id) {
      hasSyncedOnLoginRef.current = false;
      return;
    }
    if (hasSyncedOnLoginRef.current) return;
    hasSyncedOnLoginRef.current = true;

    mailsService.syncReplies().catch((syncError) => {
      console.error("Error en sync de replies al iniciar sesión:", syncError);
    });
  }, [auth?.user?.id]);

  const updateThemePreference = useCallback(async (theme) => {
    let previousTheme;

    setAuth((prev) => {
      if (!prev?.user) return prev;
      previousTheme = prev.user.theme_preference;
      return { ...prev, user: { ...prev.user, theme_preference: theme } };
    });

    try {
      const data = await authService.updateMe({ theme_preference: theme });

      setAuth((prev) => {
        if (!prev) return prev;

        return buildAuthFromMe({
          me: data,
          access: prev.access,
          refresh: prev.refresh,
          previous: prev,
        });
      });

      return data;
    } catch (error) {
      setAuth((prev) => {
        if (!prev?.user) return prev;
        return { ...prev, user: { ...prev.user, theme_preference: previousTheme } };
      });
      throw error;
    }
  }, []);

  const updateProfileName = useCallback(async ({ firstName, lastName }) => {
    const data = await authService.updateMe({
      first_name: firstName,
      last_name: lastName,
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateProfileEmail = useCallback(async ({ email }) => {
    const data = await authService.updateMe({
      email,
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateProfilePhoto = useCallback(async ({ photo }) => {
    const formData = new FormData();
    formData.append("photo", photo);

    const data = await authService.updateMe(formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateLanguagePreference = useCallback(async (language) => {
    const data = await authService.updateMe({
      language_preference: language,
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateTimezonePreference = useCallback(async (timezone) => {
    const data = await authService.updateMe({
      timezone,
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateNotifications = useCallback(async (enabled) => {
    const data = await authService.updateMe({
      notifications_enabled: enabled,
    });

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const updateReminderPreferences = useCallback(async (preferences) => {
    const data = await authService.updateMe(preferences);

    setAuth((prev) => {
      if (!prev) return prev;

      return buildAuthFromMe({
        me: data,
        access: prev.access,
        refresh: prev.refresh,
        previous: prev,
      });
    });

    return data;
  }, []);

  const refreshAccounts = useCallback(async () => {
    const [accessible, supervised] = await Promise.all([
      accountsService.list(),
      accountsService.mySupervised(),
    ]);

    setAccounts(accessible || []);
    setSupervisedAccounts(supervised || []);

    const desired = activeAccountId != null ? activeAccountId : null;

    const exists =
      desired != null &&
      (accessible || []).some((a) => Number(a?.id) === Number(desired));

    let nextActive = exists ? desired : (accessible?.[0]?.id ?? null);

    if (nextActive != null) {
      setActiveAccountId(Number(nextActive));
      localStorage.setItem("active_account_id", String(nextActive));
    } else {
      setActiveAccountId(null);
      localStorage.removeItem("active_account_id");
    }

    return {
      accs: accessible,
      supervised,
      active_account_id: nextActive,
    };
  }, [activeAccountId]);

  const refreshSession = useCallback(async () => {
    const access = await withRefreshLock(async () => {
      const data = await refreshWithLatestToken((refreshToken) =>
        authService.refresh({ refresh: refreshToken }),
      );
      if (!data) return null;

      const nextAccess = data?.access;
      if (!nextAccess) throw new Error("refresh() missing access token");

      localStorage.setItem("access_token", nextAccess);
      if (data?.refresh) localStorage.setItem("refresh_token", data.refresh);
      setAxiosAuthHeader(nextAccess);
      return nextAccess;
    });
    if (!access) return null;

    await refreshMe();
    await refreshAccounts();

    return true;
  }, [refreshAccounts, refreshMe, setAxiosAuthHeader]);

  const refreshAccessToken = useCallback(async () => {
    const access = await withRefreshLock(async () => {
      const data = await refreshWithLatestToken((refreshToken) =>
        authService.refresh({ refresh: refreshToken }),
      );
      if (!data) return null;

      const nextAccess = data?.access;
      if (!nextAccess) throw new Error("refresh() missing access token");

      localStorage.setItem("access_token", nextAccess);
      if (data?.refresh) localStorage.setItem("refresh_token", data.refresh);
      setAxiosAuthHeader(nextAccess);
      return nextAccess;
    });
    if (!access) return null;

    setAuth((prev) => {
      if (!prev) return prev;
      return { ...prev, access };
    });

    return access;
  }, [setAxiosAuthHeader]);

  const loginWithTokens = useCallback(
    async ({ access, refresh }) => {
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      setAxiosAuthHeader(access);

      const nextAuth = await refreshMe();
      await refreshAccounts();
      return nextAuth;
    },
    [refreshAccounts, refreshMe, setAxiosAuthHeader],
  );

  const loginWithPassword = useCallback(
    async ({ username, password }) => {
      const data = await authService.login({ username, password });
      const access = data?.access;
      const refresh = data?.refresh;

      if (!access || !refresh) {
        const err = new Error("Login OK but missing tokens");
        err.data = data;
        throw err;
      }

      return await loginWithTokens({ access, refresh });
    },
    [loginWithTokens],
  );

  const setActiveAccount = useCallback((id) => {
    const next = id != null ? Number(id) : null;
    setActiveAccountId(next);
    if (next != null) localStorage.setItem("active_account_id", String(next));
    else localStorage.removeItem("active_account_id");
  }, []);

  useEffect(() => {
    let mounted = true;

    async function boot() {
      setLoading(true);
      try {
        await refreshSession();
      } catch {
        if (mounted) {
          await logout();
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    boot();
    return () => {
      mounted = false;
    };
  }, [logout, refreshSession]);

  const proactiveRenewalTimerRef = useRef(null);

  useEffect(() => {
    const clearRenewalTimer = () => {
      if (proactiveRenewalTimerRef.current) {
        clearTimeout(proactiveRenewalTimerRef.current);
        proactiveRenewalTimerRef.current = null;
      }
    };

    clearRenewalTimer();

    const access = auth?.access;
    if (!access) return undefined;

    const expirationMs = getTokenExpirationMs(access);
    if (!expirationMs) return undefined;

    const renewalDelayMs = Math.max(
      expirationMs - Date.now() - ACCESS_TOKEN_RENEWAL_MARGIN_MS,
      ACCESS_TOKEN_RENEWAL_MIN_DELAY_MS,
    );

    const renewAccessToken = async () => {
      try {
        await refreshAccessToken();
      } catch (renewalError) {
        const statusCode = renewalError?.response?.status;
        if (statusCode === 400 || statusCode === 401) {
          await logout();
          return;
        }
        proactiveRenewalTimerRef.current = setTimeout(
          renewAccessToken,
          ACCESS_TOKEN_RENEWAL_RETRY_DELAY_MS,
        );
      }
    };

    proactiveRenewalTimerRef.current = setTimeout(
      renewAccessToken,
      renewalDelayMs,
    );

    return clearRenewalTimer;
  }, [auth?.access, refreshAccessToken, logout]);

  const activeAccount = useMemo(() => {
    if (!accounts?.length) return null;
    return (
      accounts.find((a) => Number(a?.id) === Number(activeAccountId)) ||
      accounts[0] ||
      null
    );
  }, [accounts, activeAccountId]);

  const requestPasswordReset = useCallback(async (email) => {
    return await authService.requestPasswordReset(email);
  }, []);

  const confirmPasswordReset = useCallback(
    async ({ uid, token, new_password, re_new_password }) => {
      return await authService.confirmPasswordReset({
        uid,
        token,
        new_password,
        re_new_password,
      });
    },
    [],
  );

  // Mutadores locales del listado de cuentas: permiten reflejar altas/cambios/
  // bajas en el contexto sin volver a pedir todo (refreshAccounts).
  const addAccount = useCallback((account) => {
    if (!account) return;
    setAccounts((prev) => [...prev, account]);
    setSupervisedAccounts((prev) => [...prev, account]);
  }, []);

  const updateAccount = useCallback((account) => {
    if (!account?.id) return;
    const apply = (list) =>
      list.map((item) =>
        item.id === account.id ? { ...item, ...account } : item,
      );
    setAccounts(apply);
    setSupervisedAccounts(apply);
  }, []);

  const removeAccount = useCallback((accountId) => {
    const apply = (list) => list.filter((item) => item.id !== accountId);
    setAccounts(apply);
    setSupervisedAccounts(apply);
  }, []);

  const value = useMemo(
    () => ({
      auth,
      setAuth,
      loading,
      accounts,
      supervisedAccounts,
      activeAccount,
      activeAccountId,
      setActiveAccount,
      addAccount,
      updateAccount,
      removeAccount,
      loginWithTokens,
      loginWithPassword,
      refreshMe,
      refreshAccounts,
      refreshSession,
      refreshAccessToken,
      updateThemePreference,
      updateLanguagePreference,
      updateTimezonePreference,
      updateProfileName,
      updateProfileEmail,
      updateProfilePhoto,
      updateNotifications,
      updateReminderPreferences,
      logout,
      requestPasswordReset,
      confirmPasswordReset,
    }),
    [
      auth,
      loading,
      accounts,
      supervisedAccounts,
      activeAccount,
      activeAccountId,
      setActiveAccount,
      addAccount,
      updateAccount,
      removeAccount,
      loginWithTokens,
      loginWithPassword,
      refreshMe,
      refreshAccounts,
      refreshSession,
      refreshAccessToken,
      updateThemePreference,
      updateLanguagePreference,
      updateTimezonePreference,
      updateProfileName,
      updateProfileEmail,
      updateProfilePhoto,
      updateNotifications,
      updateReminderPreferences,
      logout,
      requestPasswordReset,
      confirmPasswordReset,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
